import React from 'react';
import styled from 'styled-components';
import { FaEnvelope, FaLinkedin, FaTwitter, FaMapMarkerAlt, FaGraduationCap, FaUser, FaCode } from 'react-icons/fa';

const ContactSection = styled.section`
  padding: 6rem 0;
  background-color: var(--bg-light);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-medium);
  line-height: 1.6;
`;

const ContactWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ContactForm = styled.form`
  background-color: var(--white);
  padding: 2.5rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--text-dark);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-dark);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: var(--border-radius);
  font-size: 1rem;
  resize: vertical;
  min-height: 150px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
  color: var(--white);
  font-weight: 600;
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(var(--primary-rgb), 0.3);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--text-dark);
`;

const ContactText = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ContactItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ContactIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  flex-shrink: 0;
  
  svg {
    font-size: 1.125rem;
  }
`;

const ContactDetail = styled.div``;

const ContactLabel = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.25rem;
`;

const ContactValue = styled.p`
  font-size: 0.95rem;
  color: var(--text-medium);
  line-height: 1.6;
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Contact = () => {
  return (
    <ContactSection id="contact">
      <Container>
        <SectionHeader>
          <SectionTitle>Get in <span>Touch</span></SectionTitle>
          <SectionSubtitle>
            Have questions about our product or interested in early access? 
            Our founders would love to hear from you.
          </SectionSubtitle>
        </SectionHeader>
        
        <ContactWrapper>
          <ContactForm action="https://formspree.io/f/movdadzy" method="POST">
            <FormTitle>Send us a message</FormTitle>
            
            <FormGroup>
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormInput type="text" id="name" name="name" placeholder="Your name" required />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="email">Email</FormLabel>
              <FormInput type="email" id="email" name="email" placeholder="your.email@example.com" required />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="subject">Subject</FormLabel>
              <FormInput type="text" id="subject" name="subject" placeholder="What's this about?" required />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="message">Message</FormLabel>
              <FormTextarea id="message" name="message" placeholder="Tell us how we can help..." required />
            </FormGroup>
            
            <SubmitButton type="submit">Send Message</SubmitButton>
          </ContactForm>
          
          <ContactInfo>
            <ContactTitle>Our Founders</ContactTitle>
            <ContactText>
              Meet the minds behind tinysmart.AI. Our founding team brings expertise from premier 
              institutions and a passion for transforming AI-powered communications.
            </ContactText>
            
            <ContactItems>
              <ContactItem>
                <ContactIcon>
                  <FaUser />
                </ContactIcon>
                <ContactDetail>
                  <ContactLabel>Satyakam Chakravarty</ContactLabel>
                  <ContactValue>
                    Founder & Business Lead
                  </ContactValue>
                  <ContactValue>
                    <FaGraduationCap style={{ marginRight: '5px' }} /> IIM Bangalore
                  </ContactValue>
                  <ContactValue>
                    <a href="https://www.linkedin.com/in/satyakamc/" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin style={{ marginRight: '5px' }} /> linkedin.com/in/satyakamc
                    </a>
                  </ContactValue>
                </ContactDetail>
              </ContactItem>
              
              <ContactItem>
                <ContactIcon>
                  <FaCode />
                </ContactIcon>
                <ContactDetail>
                  <ContactLabel>Trupti Gavit</ContactLabel>
                  <ContactValue>
                    Founder & Tech Lead
                  </ContactValue>
                  <ContactValue>
                    <FaGraduationCap style={{ marginRight: '5px' }} /> IIT Bombay
                  </ContactValue>
                  <ContactValue>
                    <a href="https://www.linkedin.com/in/truptii-g-078829b2/" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin style={{ marginRight: '5px' }} /> linkedin.com/in/truptii-g-078829b2
                    </a>
                  </ContactValue>
                </ContactDetail>
              </ContactItem>
              
              <ContactItem>
                <ContactIcon>
                  <FaMapMarkerAlt />
                </ContactIcon>
                <ContactDetail>
                  <ContactLabel>Location</ContactLabel>
                  <ContactValue>
                    Noida, India
                  </ContactValue>
                </ContactDetail>
              </ContactItem>
            </ContactItems>
          </ContactInfo>
        </ContactWrapper>
      </Container>
    </ContactSection>
  );
};

export default Contact; 